import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  CompareArrows as CompareArrowsIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import ForecastChartApex from './ForecastChartApex';
import { forecastRunsService } from '../services/forecastruns.service';

const ForecastRuns = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedParams, setSelectedParams] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Status colors mapping
  const statusColors = {
    'STARTED': '#9e9e9e',     // Grey
    'LOADED': '#2196f3',    // Blue
    'APPROVED': '#4caf50',  // Green
    'REJECTED': '#f44336'   // Red
  };

  // Define fetchData before using it in useEffect
  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await forecastRunsService.getForecastRuns();
      
      if (result.status === 'success') {
        setRowData(result.data);
        
        // Restore selection after data refresh
        if (selectedRowId) {
          const row = result.data.find(item => item.run_id === selectedRowId);
          if (row) {
            setSelectedRow(row);
            setSelectedRows([row]);
          }
        }
      } else {
        throw new Error(result.message || 'Failed to fetch forecast runs');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term and date filter
  const filteredData = rowData.filter(row => {
    const matchesSearch = searchTerm === '' || 
      row.run_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.algorithm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.status.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = dateFilter === '' || 
      row.run_timestamp.includes(dateFilter);
    
    return matchesSearch && matchesDate;
  });

  // Pagination
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Selection handling
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows(paginatedData);
    } else {
      setSelectedRows([]);
    }
  };

  const handleClick = (event, row) => {
    const selectedIndex = selectedRows.findIndex(selected => selected.run_id === row.run_id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }

    setSelectedRows(newSelected);
    setSelectedRow(newSelected.length === 1 ? newSelected[0] : null);
    setSelectedRowId(newSelected.length === 1 ? newSelected[0].run_id : null);
  };

  const isSelected = (row) => selectedRows.findIndex(selected => selected.run_id === row.run_id) !== -1;

  const handleReviewClick = () => {
    if (selectedRows.length === 1) {
      const runId = selectedRows[0].run_id;
      console.log('Selected run_id:', runId);
      setSelectedRunIds([runId]);
      setShowChart(true);
    }
  };

  const handleCompareClick = () => {
    if (selectedRows.length > 0) {
      setSelectedRunIds(selectedRows.map(row => row.run_id));
      setShowChart(true);
    }
  };

  const handleApproveClick = async () => {
    if (!selectedRow) return;

    const runId = selectedRow.run_id;
    
    try {
      const result = await forecastRunsService.updateForecastRunStatus(runId, 'APPROVED');

      if (result.status === 'success') {
        // Show success message
        setSnackbar({
          open: true,
          message: 'Forecast run approved successfully',
          severity: 'success'
        });
        
        // Refresh the grid data
        fetchData();
      } else {
        throw new Error(result.message || 'Failed to approve forecast run');
      }
    } catch (error) {
      console.error('Error approving forecast run:', error);
      setSnackbar({
        open: true,
        message: `Error approving forecast run: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleAlgorithmClick = (parameters) => {
    setSelectedParams(parameters);
    setDialogOpen(true);
  };

  const handleHierarchyClick = (runId) => {
    navigate(`/forecast-hierarchy/${runId}`);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('');
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Forecast Planning
      </Typography>
      
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by Run ID, Algorithm, or Status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 300 }}
        />
        <TextField
          size="small"
          placeholder="Filter by date (YYYY-MM-DD)"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        {(searchTerm || dateFilter) && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < paginatedData.length}
                    checked={paginatedData.length > 0 && selectedRows.length === paginatedData.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Run ID</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Algorithm</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>SKU Count</TableCell>
                <TableCell>Store Count</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Hierarchy Populated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => {
                const isItemSelected = isSelected(row);
                return (
                  <TableRow
                    hover
                    key={row.run_id}
                    selected={isItemSelected}
                    onClick={(event) => handleClick(event, row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>{row.run_id}</TableCell>
                    <TableCell>{row.run_timestamp}</TableCell>
                    <TableCell>
                      <Tooltip title="Click to view parameters">
                        <span 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAlgorithmClick(row.parameters);
                          }}
                          style={{ 
                            cursor: 'pointer', 
                            color: '#1976d2', 
                            textDecoration: 'underline' 
                          }}
                        >
                          {row.algorithm_name}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.algorithm_version}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status}
                        size="small"
                        sx={{ 
                          backgroundColor: statusColors[row.status] || '#9e9e9e',
                          color: 'white',
                          '& .MuiChip-label': {
                            fontWeight: 500
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.sku_count}</TableCell>
                    <TableCell>{row.store_count}</TableCell>
                    <TableCell>{row.forecast_start_date}</TableCell>
                    <TableCell>{row.forecast_end_date}</TableCell>
                    <TableCell>
                      {row.hierarchy_populated ? (
                        <Tooltip title="Click to view hierarchy">
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHierarchyClick(row.run_id);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <AccountTreeIcon fontSize="small" />
                            View Hierarchy
                          </Link>
                        </Tooltip>
                      ) : (
                        <Chip 
                          label="Not Populated"
                          size="small"
                          sx={{ 
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            '& .MuiChip-label': {
                              fontWeight: 400
                            }
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Action Buttons */}
      <Stack 
        direction="row" 
        spacing={2} 
        sx={{ 
          justifyContent: 'flex-end',
          marginBottom: '20px'
        }}
      >
        <Button
          variant="contained"
          color="info"
          startIcon={<VisibilityIcon />}
          disabled={selectedRows.length !== 1}
          onClick={handleReviewClick}
        >
          Review
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutlineIcon />}
          disabled={!selectedRow || selectedRow.status === 'APPROVED'}
          onClick={handleApproveClick}
          sx={{ 
            backgroundColor: statusColors['APPROVED'],
            '&:hover': {
              backgroundColor: '#388e3c' // Darker green
            },
            '&.Mui-disabled': {
              backgroundColor: '#e0e0e0'
            }
          }}
        >
          Approve
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CompareArrowsIcon />}
          disabled={selectedRows.length < 2}
          onClick={handleCompareClick}
        >
          Compare
        </Button>
      </Stack>

      {/* Parameters Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Algorithm Parameters</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {selectedParams ? JSON.stringify(selectedParams, null, 2) : 'No parameters available'}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Chart Dialog */}
      <Dialog
        open={showChart}
        onClose={() => setShowChart(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {selectedRunIds.length > 1 ? 'Forecast Comparison' : 'Forecast Review'}
        </DialogTitle>
        <DialogContent>
          <ForecastChartApex runIds={selectedRunIds} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChart(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ForecastRuns; 